import classNames from "classnames";
import { Immutable } from "immer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { FormCheckbox } from "../../forms/FormCheckbox";
import { Modal, ModalStyles } from "../Modal";

import styles from "./index.module.scss";

type ISharedWarrantyForm = templates.WarrantyFormTemplate["value"];

type IProps = Immutable<{
    value: ISharedWarrantyForm;
    propertyName: string;
    claimURL: string;
}>;

type IModalContentProps = Immutable<{
    channels: string[];
    propertyName: string;
    onSubmit: (channel: string) => void;
}>;

type IChannelOptionProps = Immutable<{
    channel: string;
    selectedChannel: string | null;
    handleChange: (event: React.FormEvent<HTMLInputElement>) => void;
}>;

type IErrorMessageProps = Immutable<{
    errorMessage: ISharedWarrantyForm["error_message"];
    onClose: (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => void;
}>;

type ISuccessMessageProps = Immutable<{
    handleSuccess: () => void;
}>;

const SuccessMessage = React.memo(
    ({ handleSuccess }: ISuccessMessageProps): React.ReactNode => {
        const successClasses = classNames({
            [styles.resultModal]: true,
        });
        return (
            <div className={successClasses}>
                <h1 className={styles.heading}>
                    {t`Online claim submission is available to you`}
                </h1>
                <p>
                    {t`Please click below to continue filing your warranty claim online with the necessary information.`}
                </p>
                <button
                    type="button"
                    className="button"
                    onClick={handleSuccess}
                >
                    {t`File Warranty Claim`}
                </button>
            </div>
        );
    },
);

const ErrorMessage = React.memo(
    ({ errorMessage, onClose }: IErrorMessageProps): React.ReactNode => {
        const errorClasses = classNames({
            [styles.resultModal]: true,
            [styles.errorModal]: true,
        });
        return (
            <div className={errorClasses}>
                <h1 className={styles.heading}>{errorMessage?.title ?? ""}</h1>
                <p>{errorMessage?.body ?? ""}</p>
                <button type="button" className="button" onClick={onClose}>
                    {errorMessage?.button ?? ""}
                </button>
            </div>
        );
    },
);

const ChannelOption = React.memo(
    ({
        channel,
        selectedChannel,
        handleChange,
    }: IChannelOptionProps): React.ReactNode => {
        const isChecked = selectedChannel === channel;
        const classes = classNames({
            [styles.option]: true,
            [styles.checked]: isChecked,
        });
        return (
            <li className={classes}>
                <FormCheckbox
                    required={true}
                    name={channel}
                    id={channel}
                    label={channel}
                    checked={isChecked}
                    value={channel}
                    onChange={handleChange}
                    readOnly
                />
            </li>
        );
    },
);

const ModalContent = React.memo(
    ({
        channels,
        propertyName,
        onSubmit,
    }: IModalContentProps): React.ReactNode => {
        const [selectedChannel, setSelectedChannel] = useState<string | null>(
            null,
        );
        const [showAsterisk, setShowAsterisk] = useState(false);

        const handleChange = useCallback(
            (event: React.FormEvent<HTMLInputElement>) => {
                setSelectedChannel(event.currentTarget.value);
            },
            [],
        );

        const submitForm = useCallback(() => {
            if (!selectedChannel) {
                setShowAsterisk(true);
            } else {
                onSubmit(selectedChannel);
            }
        }, [selectedChannel, onSubmit]);

        const buttonClasses = classNames({
            button: true,
            [styles.uppercase]: true,
        });

        return (
            <fieldset className={styles.fieldset}>
                <legend>
                    <h1 className={styles.heading}>
                        {t`Where did you purchase your ${propertyName} product?`}
                        {showAsterisk && (
                            <span className={styles.error}>*</span>
                        )}
                    </h1>
                </legend>
                <ul className={styles.form}>
                    {channels.map((channel: string) => (
                        <ChannelOption
                            key={channel}
                            channel={channel}
                            selectedChannel={selectedChannel}
                            handleChange={handleChange}
                        />
                    ))}
                </ul>
                <button
                    type="button"
                    className={buttonClasses}
                    onClick={submitForm}
                >
                    {t`Next`}
                </button>
            </fieldset>
        );
    },
);

function setupChannelCountCSS(channelCount: number): void {
    const document = getDocument();
    document?.documentElement.style.setProperty(
        `--warranty-form--channels-row-count`,
        Math.ceil(channelCount / 2).toString() || "0",
    );
}

// TODO: This selector is fragile — a[class='button'] is an exact class match and
// the "claim" text filter assumes English. A stable data attribute (e.g.
// data-warranty-claim-btn) on the CMS button block would be more robust.
const findClaimButton = (): HTMLAnchorElement | undefined => {
    const document = getDocument();
    const buttons =
        document?.querySelectorAll<HTMLAnchorElement>("a[class='button']");
    return buttons
        ? Array.from(buttons).find((el: HTMLAnchorElement) => {
              return el.innerText.toLowerCase().includes("claim");
          })
        : undefined;
};

export function WarrantyForm(props: IProps): React.ReactNode {
    const [modalOpen, setModalOpen] = useState(false);
    const [submittedChannel, setSubmittedChannel] = useState<string | null>(
        null,
    );

    const handleSubmit = useCallback((channel: string) => {
        setSubmittedChannel(channel);
    }, []);

    const handleSuccess = useCallback(() => {
        const window = getWindow();
        if (window) {
            window.location.href = props.claimURL;
        }
    }, [props.claimURL]);

    const onClose = useCallback(
        (event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
            event.preventDefault();
            setModalOpen(false);
            setSubmittedChannel(null);
        },
        [],
    );

    useEffect(() => {
        setupChannelCountCSS(props.value.purchase_channels?.length ?? 0);
    }, [props.value.purchase_channels?.length]);

    useEffect(() => {
        const claimBtn = findClaimButton();
        if (claimBtn) {
            const handleClick = (event: MouseEvent) => {
                event.preventDefault();
                setModalOpen(true);
            };
            claimBtn.addEventListener("click", handleClick);

            return () => {
                claimBtn.removeEventListener("click", handleClick);
            };
        }
    }, []);

    const modalStyle = useMemo<ModalStyles>(
        () => ({
            content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                padding: "30px 20px",
            },
        }),
        [],
    );

    const modalClasses = useMemo(
        () =>
            classNames({
                [styles.warrantyModal]: true,
                warrantyModal: true,
            }),
        [],
    );

    const modalProps = useMemo(
        () => ({
            isOpen: modalOpen,
            aria: { modal: true as const },
            contentLabel: t`Warranty Form`,
            className: modalClasses,
            style: modalStyle,
            role: "dialog" as const,
            onRequestClose: onClose,
        }),
        [modalOpen, modalClasses, modalStyle, onClose],
    );

    let content: React.ReactNode;
    if (submittedChannel !== null) {
        // NOTE: "other" is matched against the CMS display string. If purchase_channels
        // are ever localized, this comparison must be updated or the block schema changed
        // to use a locale-independent key alongside the display label.
        if (submittedChannel.toLowerCase() === "other") {
            content = (
                <ErrorMessage
                    errorMessage={props.value.error_message}
                    onClose={onClose}
                />
            );
        } else {
            content = <SuccessMessage handleSuccess={handleSuccess} />;
        }
    } else {
        content = (
            <ModalContent
                key={String(modalOpen)}
                channels={props.value.purchase_channels ?? []}
                propertyName={props.propertyName}
                onSubmit={handleSubmit}
            />
        );
    }

    return <Modal {...modalProps}>{content}</Modal>;
}
